<script setup>
import { loginPagePath } from '@/utils/loginFlow'
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useIntervalFn, useTimeoutFn } from '@vueuse/core'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import {
  completePendingPaidExamAfterRecharge,
  getPendingGuestExamId,
  isInsufficientBalanceError
} from '@/utils/examClaim'

const route = useRoute()
const router = useRouter()

const channel = ref('wechat')
const payMode = ref(false)
const payInfo = ref(null)
const payStatus = ref('active')
const amount = ref(10)
const balance = ref(0)
const finishingExam = ref(false)
const payee = ref({ payeeName: '', payeeBank: '', payeeAccount: '', remarkHint: '' })
const bankForm = ref({
  amountYuan: 10,
  payerType: 'personal',
  payerName: '',
  payerBank: '',
  payerAccount: '',
  payeeAccount: '',
  transferAt: '',
  remark: '',
  note: ''
})
const bankSubmitting = ref(false)
const bankSlips = ref([])

const pendingExamId = computed(() => {
  if (route.query.from === 'exam' && route.query.examId) {
    return String(route.query.examId)
  }
  return getPendingGuestExamId()
})

const hasPendingExam = computed(() => !!pendingExamId.value)

// Format amount helper
function formatAmount(value) {
  if (!value && value !== 0) return '0.00';
  return (Number(value) / 100).toFixed(2);
}

// Timer functions
const { pause, resume } = useIntervalFn(checkPayStatus, 2000);
const { start, stop } = useTimeoutFn(handlePayExpired, 1000 * 60);

function handleTimerDone() {
  stop();
  pause();
}

// API calls
async function getBalance() {
  try {
    const token = localStorage.getItem('jwt_token')
    const response = await fetch('/api/v1/user/balance', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    if (data.code === 200 && data.data && data.data.length > 0) {
      // Convert cents to yuan and round to 2 decimal places
      balance.value = data.data[0].balance;
    }
  } catch (error) {
    console.error('Failed to fetch balance:', error);
  }
}

function authToken() {
  return localStorage.getItem('jwt_token')
}

function authHeaders() {
  return {
    Authorization: `Bearer ${authToken()}`,
    'Content-Type': 'application/json'
  }
}

async function loadPayee() {
  try {
    const res = await fetch('/api/v1/recharge/bank/payee', { headers: authHeaders() })
    const data = await res.json()
    if (data.code === 200 && data.data) {
      payee.value = data.data
      if (data.data.payeeAccount && !bankForm.value.payeeAccount) {
        bankForm.value.payeeAccount = data.data.payeeAccount
      }
    }
  } catch (e) {
    console.error('Failed to load payee', e)
  }
}

async function loadBankSlips() {
  try {
    const res = await fetch('/api/v1/recharge/bank/slip/list', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ page: 1, pageSize: 10 })
    })
    const data = await res.json()
    if (data.code === 200) {
      bankSlips.value = data.data?.items || []
    }
  } catch (e) {
    console.error('Failed to load bank slips', e)
  }
}

function slipStatusLabel(s) {
  return ({ pending: '待审核', approved: '已入账', rejected: '已拒绝' })[s] || s
}

async function submitBankSlip() {
  const cents = Math.round(Number(bankForm.value.amountYuan || 0) * 100)
  if (cents <= 0) {
    ElMessage.warning('请填写转账金额')
    return
  }
  if (!bankForm.value.payerName || !bankForm.value.payerBank || !bankForm.value.payeeAccount || !bankForm.value.remark || !bankForm.value.transferAt) {
    ElMessage.warning('请填写转账人/公司、付款银行、转入账号、附言和转账日期')
    return
  }
  bankSubmitting.value = true
  try {
    const transferAt = Math.floor(new Date(bankForm.value.transferAt + 'T00:00:00').getTime() / 1000)
    const res = await fetch('/api/v1/recharge/bank/slip/submit', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        amountCents: cents,
        payerType: bankForm.value.payerType,
        payerName: bankForm.value.payerName,
        payerBank: bankForm.value.payerBank,
        payerAccount: bankForm.value.payerAccount,
        payeeName: payee.value.payeeName,
        payeeBank: payee.value.payeeBank,
        payeeAccount: bankForm.value.payeeAccount,
        transferAt,
        remark: bankForm.value.remark,
        note: bankForm.value.note
      })
    })
    const data = await res.json()
    if (data.code !== 200) {
      ElMessage.error(data.message || '提交失败')
      return
    }
    ElMessage.success(data.message || '单据已提交，等待审核入账')
    bankForm.value.remark = ''
    bankForm.value.note = ''
    await loadBankSlips()
    await getBalance()
  } catch (e) {
    ElMessage.error(e.message || '提交失败')
  } finally {
    bankSubmitting.value = false
  }
}

async function createRecharge() {
  try {
    const token = localStorage.getItem('jwt_token')
    const res = await fetch('/api/v1/recharge/create', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        payMethod: 1,
        payMoney: amount.value * 100, // Convert to cents
      }),
    });
    const response = await res.json();
    if (response.code === 200 && response.data) {
      return response.data;
    }
    if (response.code === 401) {
      
      window.location.href = loginPagePath(window.location.pathname + window.location.search)
    }

    throw new Error(response.message || '创建充值订单失败');
  } catch (error) {
    console.error('Failed to create recharge:', error);
    throw error;
  }
}

async function checkPayStatus() {
  if (!payMode.value || payStatus.value === 'loading' || !payInfo.value?.orderNo) return;
  
  try {
    const token = localStorage.getItem('jwt_token')
    const res = await fetch(`/api/v1/recharge/getorder`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        orderNo: payInfo.value.orderNo
      })
    });
    const response = await res.json();
    
    if (response.code !== 200) {
      console.error('Failed to check payment status:', response.message);
      return;
    }

    const data = response.data;
    if (data.status === 'pending') return;
    if (data.status === 'completed') {
      handlePaySuccess();
    } else if (['rejected', 'failed'].includes(data.status)) {
      handlePayFailed();
    }
  } catch (error) {
    console.error('Failed to check payment status:', error);
  }
}

// Event handlers
async function handleSubmit() {
  try {
    handleTimerDone();
    payMode.value = true;
    payStatus.value = 'loading';
    
    payInfo.value = await createRecharge();
    console.log('payInfo:', payInfo.value);
    console.log('payInfo.url:', payInfo.value?.url);
    resume();
    start();
  } catch (error) {
    console.error(error);
    payMode.value = false;
    ElMessage.error('支付码生成失败');
  } finally {
    payStatus.value = 'active';
  }
}

function handlePayFailed() {
  handleTimerDone();
  ElMessage.error('支付失败');
  payMode.value = false;
}

function handlePayCancel() {
  handleTimerDone();
  payMode.value = false;
  ElMessage.error('支付取消');
}

function handlePayExpired() {
  handleTimerDone();
  payStatus.value = 'expired';
  ElMessage.error('支付码已过期');
  payMode.value = false;
}

async function tryFinishPendingExam() {
  const examId = pendingExamId.value
  if (!examId || finishingExam.value) return false

  finishingExam.value = true
  try {
    const resultId = await completePendingPaidExamAfterRecharge(router, examId)
    return !!resultId
  } catch (err) {
    if (!isInsufficientBalanceError(err)) {
      ElMessage.error(err.message || '购买测评失败')
    }
    return false
  } finally {
    finishingExam.value = false
  }
}

async function handlePaySuccess() {
  handleTimerDone();
  payStatus.value = 'scanned';
  await getBalance();
  ElMessage.success('支付成功');

  if (hasPendingExam.value) {
    const done = await tryFinishPendingExam()
    if (done) return
    ElMessage.info('请确认充值金额足够支付测评费用，或继续充值后点击「支付完成」')
  }

  setTimeout(() => {
    payMode.value = false;
  }, 1500);
}

onMounted(async () => {
  await getBalance();
  await loadPayee();
  await loadBankSlips();
  if (hasPendingExam.value) {
    await tryFinishPendingExam()
  }
});
</script>

<template>
  <div class="recharge-container recharge-page">
    <div class="card">
      <h2>充值</h2>
      <div v-if="hasPendingExam" class="exam-pending-banner">
        您已完成测评答题，充值成功后将<strong>自动扣费购买测评</strong>并展示报告。
      </div>
      <div class="content">
        <div class="balance-info">
          <span>当前余额:</span>
          <span>{{ formatAmount(balance) }} 元</span>
        </div>

        <div class="channel-tabs">
          <button :class="{ active: channel === 'wechat' }" @click="channel = 'wechat'">微信支付</button>
          <button :class="{ active: channel === 'bank' }" @click="channel = 'bank'">银行转账</button>
        </div>

        <div v-if="channel === 'wechat'">
        <p class="channel-hint">微信扫码支付成功后实时入账，无需后台审核。</p>
        <div v-if="!payMode" class="recharge-form">
          <div class="form-item">
            <label>金额(元)</label>
            <input 
              type="number" 
              v-model="amount"
              min="0.1"
              max="1000000"
              step="0.1"
              placeholder="请输入金额"
            />
          </div>
          <button class="submit-btn" @click="handleSubmit">确认充值</button>
        </div>

        <div v-else class="payment-info">
          <div class="amount-info">
            <span>金额:</span>
            <span>{{ formatAmount(payInfo?.amount) }} 元</span>
          </div>
          <div class="qrcode-section">
            <p>请使用微信扫码支付</p>
            <div class="qrcode-wrapper">
              <vue-qrcode
                v-if="payInfo?.url"
                :value="payInfo.url"
                tag="svg"
                :options="{
                  width: 200,
                  height: 200,
                  margin: 0,
                  color: {
                    dark: '#000000',
                    light: '#ffffff'
                  }
                }"
                :class="{ 'qrcode-loading': payStatus === 'loading', 'qrcode-expired': payStatus === 'expired' }"
              />
              <div v-if="payStatus === 'loading'" class="qrcode-overlay">
                <span>加载中...</span>
              </div>
              <div v-if="payStatus === 'expired'" class="qrcode-overlay">
                <span>二维码已过期</span>
                <button @click="handleSubmit">刷新</button>
              </div>
            </div>
            <div class="payment-buttons">
              <button @click="handlePaySuccess">支付完成</button>
              <button @click="handlePayCancel">取消支付</button>
            </div>
          </div>
        </div>
        </div>

        <div v-else class="bank-panel">
          <p class="channel-hint">对公/个人转账后填写单据，后台核对通过后入账。微信支付宝订单不在此提交。</p>
          <div v-if="payee.payeeAccount || payee.payeeName" class="payee-card">
            <h3>收款账户</h3>
            <p v-if="payee.payeeName">户名：{{ payee.payeeName }}</p>
            <p v-if="payee.payeeBank">开户行：{{ payee.payeeBank }}</p>
            <p v-if="payee.payeeAccount">账号：{{ payee.payeeAccount }}</p>
            <p class="payee-hint">{{ payee.remarkHint }}</p>
          </div>
          <div class="recharge-form">
            <div class="form-item">
              <label>转账金额(元)</label>
              <input type="number" v-model="bankForm.amountYuan" min="0.1" step="0.1" />
            </div>
            <div class="form-item">
              <label>转账方</label>
              <div class="payer-type">
                <label><input type="radio" value="personal" v-model="bankForm.payerType" /> 个人</label>
                <label><input type="radio" value="company" v-model="bankForm.payerType" /> 公司</label>
              </div>
            </div>
            <div class="form-item">
              <label>转账个人/公司名称</label>
              <input v-model="bankForm.payerName" placeholder="付款方名称" />
            </div>
            <div class="form-item">
              <label>付款银行</label>
              <input v-model="bankForm.payerBank" placeholder="如：中国工商银行" />
            </div>
            <div class="form-item">
              <label>付款账号</label>
              <input v-model="bankForm.payerAccount" placeholder="选填" />
            </div>
            <div class="form-item">
              <label>转入账号</label>
              <input v-model="bankForm.payeeAccount" placeholder="收款账号" />
            </div>
            <div class="form-item">
              <label>转账日期</label>
              <input type="date" v-model="bankForm.transferAt" />
            </div>
            <div class="form-item">
              <label>附言</label>
              <input v-model="bankForm.remark" placeholder="银行转账附言，建议填写用户ID" />
            </div>
            <div class="form-item">
              <label>补充说明</label>
              <textarea v-model="bankForm.note" rows="2" placeholder="选填"></textarea>
            </div>
            <button class="submit-btn" :disabled="bankSubmitting" @click="submitBankSlip">提交转账单据</button>
          </div>
          <h3 class="slip-title">我的转账单据</h3>
          <ul class="slip-list">
            <li v-for="row in bankSlips" :key="row.id">
              <span>{{ row.slipNo }}</span>
              <span>¥{{ formatAmount(row.amountCents) }}</span>
              <span>{{ slipStatusLabel(row.status) }}</span>
            </li>
            <li v-if="!bankSlips.length" class="empty">暂无单据</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.recharge-container {
  padding: 20px;
  max-width: 880px;
  margin: 0 auto;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.content {
  margin-top: 20px;
}

.exam-pending-banner {
  margin-top: 16px;
  padding: 12px 16px;
  background: #eef9f3;
  border-left: 4px solid #42b983;
  border-radius: 0 6px 6px 0;
  font-size: 14px;
  color: #333;
}

.balance-info {
  margin-bottom: 20px;
  font-size: 16px;
}

.channel-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.channel-tabs button {
  -webkit-appearance: none;
  appearance: none;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #303133;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
}
.channel-tabs button.active {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}
.channel-hint {
  margin: 0 0 16px;
  font-size: 13px;
  color: #5c6370;
}
.payee-card {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.payee-card h3 { margin: 0 0 8px; font-size: 15px; }
.payee-card p { margin: 4px 0; font-size: 14px; }
.payee-hint { color: #e6a23c; }
.payer-type { display: flex; gap: 16px; }
.form-item textarea {
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}
.slip-title { margin: 24px 0 8px; font-size: 15px; }
.slip-list { list-style: none; padding: 0; margin: 0; }
.slip-list li { display: flex; gap: 12px; font-size: 13px; padding: 6px 0; border-bottom: 1px solid #ebeef5; }
.slip-list .empty { color: #909399; border: none; }

.recharge-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-item input {
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.submit-btn {
  -webkit-appearance: none;
  appearance: none;
  background: #1890ff;
  color: #fff;
  border: none;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
}

.submit-btn:hover {
  background: #40a9ff;
  color: #fff;
}

.submit-btn:disabled {
  background: #8cc8ff;
  color: #fff;
  cursor: not-allowed;
}

.payment-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.qrcode-section {
  text-align: center;
}

.qrcode-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 20px auto;
  background: #fff;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qrcode-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qrcode-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  gap: 10px;
}

.qrcode-overlay button {
  -webkit-appearance: none;
  appearance: none;
  padding: 5px 15px;
  background: #1890ff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.qrcode-overlay button:hover {
  background: #40a9ff;
}

.payment-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.payment-buttons button {
  -webkit-appearance: none;
  appearance: none;
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  color: #303133;
}

.payment-buttons button:first-child {
  background: #52c41a;
  color: #fff;
  border: none;
}

.payment-buttons button:last-child {
  background: #fff;
  color: #303133;
}

@media (max-width: 767px) {
  .recharge-container {
    padding: 0;
  }

  .card {
    padding: 12px;
    border-radius: 0;
    box-shadow: none;
  }

  .payment-buttons {
    flex-direction: column;
    width: 100%;
  }

  .payment-buttons button {
    width: 100%;
  }
}
</style> 